import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { format, addMonths, subMonths } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Dumbbell, UtensilsCrossed, Heart, Moon, Timer, Scale, Activity, CheckCircle2, Circle } from 'lucide-react';

const STATUS = { green: '#65A30D', yellow: '#FFB800', red: '#FF5C5C' };

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);

  useEffect(() => {
    api.get('/calendar', { params: { month: format(cursor, 'yyyy-MM') } }).then(r => setData(r.data));
  }, [cursor]);

  useEffect(() => {
    if (!sel) { setDayDetail(null); return; }
    setDayDetail(null);
    api.get('/calendar/day', { params: { date: sel.date } }).then(r => setDayDetail(r.data)).catch(() => {});
  }, [sel]);

  const s = data?.summary;
  const summaryCards = [
    { label: 'YourReGen Avg', value: s?.regen_avg ?? '—', color: '#65A30D', icon: Activity },
    { label: 'Training', value: s != null ? `${s.training}%` : '—', color: '#00C8FF', icon: Dumbbell },
    { label: 'Nutrition', value: s != null ? `${s.nutrition}%` : '—', color: '#65A30D', icon: UtensilsCrossed },
    { label: 'Recovery', value: s != null ? `${s.recovery}%` : '—', color: '#A78BFA', icon: Heart },
    { label: 'Weight', value: s != null ? `${s.weight_change > 0 ? '+' : ''}${s.weight_change}kg` : '—', color: '#00C8FF', icon: Scale },
  ];

  return (
    <div className="space-y-5" data-testid="client-calendar">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mini text-[#00C8FF]">Planning & Accountability</div>
          <h1 className="font-display font-black text-3xl mt-1">{format(cursor, 'MMMM yyyy')}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCursor(c => subMonths(c, 1))} data-testid="cal-prev" className="w-9 h-9 rounded-full regen-card flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(c => addMonths(c, 1))} data-testid="cal-next" className="w-9 h-9 rounded-full regen-card flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Monthly summary */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="month-summary">
        {summaryCards.map(c => (
          <div key={c.label} className="regen-card-elevated p-3 text-center">
            <c.icon className="w-4 h-4 mx-auto" style={{ color: c.color }} />
            <div className="font-mono text-base mt-1" style={{ color: c.color }}>{c.value}</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="regen-card-elevated p-4">
        <div className="grid grid-cols-7 gap-1 mb-2 text-center">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="label-mini py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: data?.first_dow || 0 }).map((_, i) => <div key={`b${i}`} />)}
          {(data?.days || []).map(day => {
            const isToday = day.date === format(new Date(), 'yyyy-MM-dd');
            const col = STATUS[day.status];
            return (
              <button key={day.date} onClick={() => setSel(day)} data-testid={`cal-day-${day.date}`}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center border transition active:scale-95 ${isToday ? 'border-[#00C8FF]' : 'border-[var(--c-hair)]'}`}
                style={col ? { background: `${col}1f`, borderColor: `${col}66` } : {}}>
                <div className={`text-sm font-mono ${isToday ? 'text-[#00C8FF]' : ''}`}>{day.date.slice(8)}</div>
                {day.completion != null && <div className="text-[8px] font-mono leading-tight" style={{ color: col }}>{day.completion}%</div>}
                {day.score != null
                  ? <div className="text-[9px] font-mono font-bold leading-tight text-[var(--c-green-ink)]">{day.score}</div>
                  : (col && <span className="w-1.5 h-1.5 rounded-full mt-0.5" style={{ background: col, boxShadow: `0 0 5px ${col}` }} />)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[var(--c-green)]" />Completed</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#FFB800]" />Partial</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#FF5C5C]" />Missed</div>
      </div>

      {/* Day Detail */}
      <Dialog open={!!sel} onOpenChange={o => !o && setSel(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] lg:max-w-2xl" data-testid="day-detail">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{format(new Date(sel.date), 'EEEE, MMM d')}</span>
                  {sel.score != null && <span className="font-mono text-[var(--c-green-ink)] text-lg">{sel.score}</span>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {sel.completion != null && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${sel.completion}%`, background: STATUS[sel.status] }} /></div>
                    <span className="font-mono text-sm" style={{ color: STATUS[sel.status] }}>{sel.completion}%</span>
                  </div>
                )}
                <PillarSection icon={Dumbbell} color="#00C8FF" label="Training" items={dayDetail?.training} keyName="cal-training" />
                <PillarSection icon={UtensilsCrossed} color='#65A30D' label="Nutrition" items={dayDetail?.nutrition} keyName="cal-nutrition" slotLabel />
                <PillarSection icon={Heart} color="#A78BFA" label="Recovery" items={dayDetail?.recovery} keyName="cal-recovery" />
                <div className="border-t border-[var(--c-hair)] pt-3 space-y-2.5">
                  <DetailRow icon={Moon} color="#A78BFA" label="Sleep" value={sel.sleep || '—'} sub={sel.sleep_quality ? `Quality ${sel.sleep_quality}/10` : ''} empty={!sel.sleep} />
                  <DetailRow icon={Timer} color="#00C8FF" label="Fasting" value={sel.fasting || '—'} empty={!sel.fasting} />
                  <DetailRow icon={Scale} color="#00C8FF" label="Weight" value={sel.weight != null ? `${sel.weight} kg` : '—'} empty={sel.weight == null} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PillarSection({ icon: Icon, color, label, items, keyName, slotLabel }) {
  if (!items || items.length === 0) return null;
  const done = items.filter(i => i.done).length;
  return (
    <div data-testid={`${keyName}-section`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-semibold">{label}</span>
        </div>
        <span className="font-mono text-xs" style={{ color }}>{done}/{items.length}</span>
      </div>
      <div className="space-y-1 pl-6">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-sm" data-testid={`${keyName}-item-${i}`}>
            {it.done ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} /> : <Circle className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/50" />}
            <span className={it.done ? 'text-muted-foreground' : ''}>{slotLabel && it.slot ? `${it.slot}: ` : ''}{it.name}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wide" style={{ color: it.done ? color : '#7E8C9E' }}>{it.done ? 'Completed' : 'Pending'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, color, label, value, sub, empty }) {
  return (
    <div className={`flex items-center gap-3 ${empty ? 'opacity-40' : ''}`}>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}
